import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { api, i18n } from "@/lib/api";

const Ctx = createContext(null);
export const useApp = () => useContext(Ctx);

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null=checking, false=anon, obj=auth
  const [lang, setLang] = useState(localStorage.getItem("rai_lang") || "it");
  const [theme, setTheme] = useState(localStorage.getItem("rai_theme") || "light");
  const [properties, setProperties] = useState([]);
  const [activeId, setActiveId] = useState(localStorage.getItem("rai_active") || null);

  const t = useCallback((k) => i18n[lang][k] || k, [lang]);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("rai_theme", theme);
  }, [theme]);
  useEffect(() => { localStorage.setItem("rai_lang", lang); }, [lang]);

  const loadProperties = useCallback(async () => {
    try {
      const { data } = await api.get("/properties");
      setProperties(data);
      if (data.length && !localStorage.getItem("rai_active")) {
        setActiveId(data[0].id);
        localStorage.setItem("rai_active", data[0].id);
      }
      return data;
    } catch { return []; }
  }, []);

  useEffect(() => {
    if ((window.location.hash || "").includes("session_id=")) return; // OAuth callback in progress
    (async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        await loadProperties();
      } catch { setUser(false); }
    })();
  }, [loadProperties]);

  // Silent sliding-session refresh: keep the session alive while the app is open
  // (periodic + on tab focus) so active users are never abruptly logged out.
  useEffect(() => {
    if (!user) return;
    let cancelled = false;
    const refresh = async () => {
      try {
        const { data } = await api.post("/auth/refresh");
        if (!cancelled && data?.token) localStorage.setItem("rai_token", data.token);
      } catch { /* 401 handled globally by the axios interceptor */ }
    };
    refresh();
    const id = setInterval(refresh, 20 * 60 * 1000); // every 20 minutes
    const onVis = () => { if (document.visibilityState === "visible") refresh(); };
    document.addEventListener("visibilitychange", onVis);
    return () => { cancelled = true; clearInterval(id); document.removeEventListener("visibilitychange", onVis); };
  }, [user]);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    localStorage.setItem("rai_token", data.token);
    try { const me = await api.get("/auth/me"); setUser(me.data); } catch { setUser(data.user); }
    await loadProperties();
  };
  const refreshUser = useCallback(async () => {
    try { const { data } = await api.get("/auth/me"); setUser(data); return data; } catch { return null; }
  }, []);
  const register = async (email, password, name) => {
    const { data } = await api.post("/auth/register", { email, password, name });
    localStorage.setItem("rai_token", data.token);
    setUser(data.user);
    await loadProperties();
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch {}
    localStorage.removeItem("rai_token");
    setUser(false); setProperties([]); setActiveId(null);
  };
  const selectProperty = (id) => { setActiveId(id); localStorage.setItem("rai_active", id); };

  const impersonate = async (uid) => {
    const { data } = await api.post(`/admin/users/${uid}/impersonate`);
    localStorage.setItem("rai_token", data.token);
    localStorage.removeItem("rai_active");
    window.location.href = "/dashboard";
  };
  const stopImpersonation = async () => {
    const { data } = await api.post("/auth/stop-impersonation");
    localStorage.setItem("rai_token", data.token);
    localStorage.removeItem("rai_active");
    window.location.href = "/admin";
  };

  const deleteProperty = async (id) => {
    await api.delete(`/properties/${id}`);
    const fresh = await loadProperties();
    if (activeId === id) {
      const next = fresh.find((p) => p.id !== id) || fresh[0];
      if (next) { setActiveId(next.id); localStorage.setItem("rai_active", next.id); }
      else { setActiveId(null); localStorage.removeItem("rai_active"); }
    }
    return fresh;
  };

  return (
    <Ctx.Provider value={{
      user, setUser, lang, setLang, theme, setTheme, t,
      properties, loadProperties, activeId, selectProperty, deleteProperty,
      login, register, logout, refreshUser, impersonate, stopImpersonation,
    }}>
      {children}
    </Ctx.Provider>
  );
}
